/**
 * The 17 business rules (spec § 6), as pure functions.
 *
 * Every one is transcribed from the prototype rather than paraphrased from the
 * spec prose — the spec's own rule is that the prototype wins. Rule ids are in
 * the doc comments so a screen can be traced back to the specification.
 *
 * Pure by design: no IndexedDB, no React, no Date.now() except where a rule is
 * explicitly about "now" (T1, E1, F1). That is what makes them testable, and
 * the landed-cost and margin maths is exactly where a silent error would cost
 * the buyer real money.
 */

import {
  DEFAULT_LANDED_FACTOR,
  MARGIN_GOOD_PCT,
  MARGIN_OK_PCT,
  QUOTE_EXPIRY_DAYS,
  MAIN_PHOTO_TAG,
} from '../data/constants';
import { num, money, today, startOfToday, daysUntil } from './format';
import { newFollowUp } from '../data/entities';

/* ═══════════════════════════════════════════════════════════════════════════
   FX1 — currency conversion only via per-fair rates. No live FX, ever.
   ═════════════════════════════════════════════════════════════════════════ */

/** 1 unit of `currency` → USD for this fair. 0 when the rate is unset. */
export function fxRate(currency, fair) {
  if (!currency || currency === 'USD') return 1;
  const r = fair && fair.fx ? num(fair.fx[currency]) : 0;
  return r || 0;
}

/** Converted amount, or null when no rate is set — never a silent zero. */
export function toUSD(amount, currency, fair) {
  const r = fxRate(currency, fair);
  return r ? amount * r : null;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Derived supplier values — COMPUTED, NEVER STORED.
   Persisting any of these would drift from the products beneath them.
   ═════════════════════════════════════════════════════════════════════════ */

/** Strong if any product is Strong, else Maybe if any is, else No, else Maybe. */
export function supplierDecision(supplier) {
  const products = (supplier && supplier.products) || [];
  if (products.some((p) => p.decision === 'Strong')) return 'Strong';
  if (products.some((p) => p.decision === 'Maybe')) return 'Maybe';
  if (products.length) return 'No';
  return 'Maybe';
}

/** The MAX product rating, not an average. */
export function supplierRating(supplier) {
  const products = (supplier && supplier.products) || [];
  return products.reduce((max, p) => Math.max(max, p.rating || 0), 0);
}

/**
 * Main photo, with the prototype's four-step fallback:
 * this product's main → any main → any Product → the first photo.
 */
export function mainPhoto(supplier, productId) {
  const photos = (supplier && supplier.photos) || [];
  return (
    photos.find((p) => p.tag === MAIN_PHOTO_TAG && (!productId || p.productId === productId)) ||
    photos.find((p) => p.tag === MAIN_PHOTO_TAG) ||
    photos.find((p) => p.tag === 'Product') ||
    photos[0] ||
    null
  );
}

/** Rule C1's reader: the primary contact, falling back to the first. */
export function primaryContact(supplier) {
  const contacts = (supplier && supplier.contacts) || [];
  return contacts.find((c) => c.primary) || contacts[0] || null;
}

/** Open follow-ups — anything not Done. */
export function pendingFollowUps(supplier) {
  return ((supplier && supplier.followUps) || []).filter((f) => f.status !== 'Done').length;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Q1 — Quick Entry save
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Requires a product name OR a company. Returns the defaults to apply:
 * missing name → "Product N", missing company → "Unknown supplier".
 */
export function quickEntryDefaults({ productName, company, productIndex = 0 }) {
  const hasName = !!(productName && productName.trim());
  const hasCompany = !!(company && company.trim());
  if (!hasName && !hasCompany) {
    return { ok: false, reason: 'Add a product name or a company first.' };
  }
  return {
    ok: true,
    productName: hasName ? productName.trim() : `Product ${productIndex + 1}`,
    company: hasCompany ? company.trim() : 'Unknown supplier',
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   D1 — duplicate warning
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * On blur of Company: match any supplier in the SAME FAIR by company
 * (case-insensitive). Returns the match, or null.
 *
 * `excludeId` keeps a supplier from matching itself while being edited.
 */
export function findDuplicate({ suppliers, fairId, company, excludeId }) {
  const c = (company || '').trim().toLowerCase();
  if (!c) return null;

  return (
    (suppliers || []).find((s) => {
      if (s.id === excludeId) return false;
      if (s.deletedAt) return false;
      if (fairId && s.fairId !== fairId) return false;
      return (s.company || '').trim().toLowerCase() === c;
    }) || null
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   S1 — shortlist sync
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * inShortlist = any product is Strong. If no Strong remains it LEAVES the
 * shortlist unless manualShortlist is set (by "Add a Maybe" or the Overview
 * toggle). Returns a new supplier; never mutates.
 */
export function syncShortlist(supplier) {
  const hasStrong = (supplier.products || []).some((p) => p.decision === 'Strong');
  if (hasStrong) return { ...supplier, inShortlist: true };
  if (supplier.manualShortlist) return supplier;
  return { ...supplier, inShortlist: false };
}

/* ═══════════════════════════════════════════════════════════════════════════
   S2 — shortlist messaging depends on the fair's stage
   ═════════════════════════════════════════════════════════════════════════ */

export function shortlistMode(fair) {
  const complete = !!fair && fair.status === 'complete';
  return complete
    ? {
        complete: true,
        banner: 'China research complete.',
        lead: "Now let's determine the true cost and supplier risk.",
        buttonLabel: 'Review',
        showLanded: true,
      }
    : {
        complete: false,
        banner: 'Your strongest suppliers will appear here.',
        lead: 'Mark a product "Strong" at the booth and it lands on this list.',
        buttonLabel: 'Open',
        showLanded: false,
      };
}

/* ═══════════════════════════════════════════════════════════════════════════
   F1 / F2 — automatic follow-ups
   ═════════════════════════════════════════════════════════════════════════ */

export const NEXT_TO_FU = {
  'Get Sample': 'Request Sample',
  'Ask Freight': 'Request Freight Information',
  'Negotiate Again': 'Negotiate Terms',
  'Verify Certification': 'Request Certificate',
  'Follow Up': 'Other',
  Order: 'Other',
};

/**
 * F2 then F1, matching the prototype's order.
 *
 * F2: revisit && revisitAt → an Appointment follow-up, date/time split on 'T'.
 * F1: nextStep → its mapped type, notes prefixed "Place order: " for Order.
 *
 * Neither creates a duplicate while an OPEN follow-up of the same type already
 * exists for that product — so re-saving a supplier doesn't pile them up.
 * Returns a new followUps array.
 */
export function autoFollowUp(supplier, product) {
  const followUps = [...((supplier && supplier.followUps) || [])];

  const hasOpen = (type) =>
    followUps.some((f) => f.productId === product.id && f.type === type && f.status !== 'Done');

  // F2 — appointment from the revisit time.
  if (product.revisit && product.revisitAt && !hasOpen('Appointment')) {
    const [date, time] = String(product.revisitAt).split('T');
    followUps.push(
      newFollowUp({
        type: 'Appointment',
        date,
        time: time || '',
        notes: product.revisitReason || product.name,
        productId: product.id,
        auto: true,
      }),
    );
  }

  // F1 — next step.
  if (product.nextStep && product.nextStep !== 'None') {
    const type = NEXT_TO_FU[product.nextStep] || 'Other';
    if (!hasOpen(type)) {
      followUps.push(
        newFollowUp({
          type,
          date: today(),
          notes: (product.nextStep === 'Order' ? 'Place order: ' : '') + product.name,
          productId: product.id,
          auto: true,
        }),
      );
    }
  }

  return followUps;
}

/* ═══════════════════════════════════════════════════════════════════════════
   P1 — the price line
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * money(best||quoted) + "/unit" when not per-piece + " ≈ $X" when not USD.
 * The USD approximation is OMITTED ENTIRELY when the fair has no rate set —
 * showing "≈ $0.00" would be worse than showing nothing.
 */
export function priceLine(product, fair) {
  if (!product || !product.price) return '';
  const v = num(product.bestPrice || product.price);
  let t = money(v, product.currency);
  if (product.priceUnit && product.priceUnit !== 'pc') t += `/${product.priceUnit}`;
  if (product.currency !== 'USD') {
    const usd = toUSD(v, product.currency, fair);
    if (usd != null) t += ` ≈ ${money(usd)}`;
  }
  return t;
}

/** The best quoted price across a supplier's products, in USD. */
export function bestQuoted(supplier, fair) {
  const prices = ((supplier && supplier.products) || [])
    .filter((p) => p.price || p.bestPrice)
    .map((p) => {
      const v = num(p.bestPrice || p.price);
      return p.currency === 'USD' ? v : toUSD(v, p.currency, fair);
    })
    .filter((v) => v != null && v > 0);
  return prices.length ? Math.min(...prices) : null;
}

/* ═══════════════════════════════════════════════════════════════════════════
   M1 — margin estimate
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Needs both a price and a sell price. Returns null otherwise — a margin
 * without a sell price is not a zero, it is unknown.
 *
 *   landedEst = usd × landedFactor (default 1.35)
 *   margin%   = (sell − landedEst) / sell × 100
 *   profit    = sell − landedEst
 */
export function marginCalc(product, fair, settings) {
  const v = num(product.bestPrice || product.price);
  if (!v || !product.sellPrice) return null;

  const usd = product.currency === 'USD' ? v : toUSD(v, product.currency, fair);
  if (usd == null) return null;

  const factor = num(settings && settings.landedFactor) || DEFAULT_LANDED_FACTOR;
  const landedEst = usd * factor;
  const sell = num(product.sellPrice);
  const margin = sell ? ((sell - landedEst) / sell) * 100 : 0;

  return { landedEst, margin, profit: sell - landedEst, factor };
}

/** ≥40% green, ≥25% amber, else red. */
export function marginTone(marginPct) {
  if (marginPct >= MARGIN_GOOD_PCT) return 'green';
  if (marginPct >= MARGIN_OK_PCT) return 'amber';
  return 'red';
}

/* ═══════════════════════════════════════════════════════════════════════════
   L1 — landed cost (Final Review)
   ═════════════════════════════════════════════════════════════════════════ */

export const LANDED_EXTRA_KEYS = [
  'tc_freight',
  'tc_broker',
  'tc_duty',
  'tc_inland',
  'tc_insurance',
  'tc_inspection',
  'tc_other',
];

/**
 * total   = tc_price × tc_qty + all the extras
 * perUnit = total / qty, or NULL when qty is 0 — not Infinity, not 0.
 */
export function landed(supplier) {
  const r = (supplier && supplier.review) || {};
  const qty = num(r.tc_qty);
  const unit = num(r.tc_price);
  const extras = LANDED_EXTRA_KEYS.reduce((a, k) => a + num(r[k]), 0);
  const total = unit * qty + extras;
  return { total, perUnit: qty > 0 ? total / qty : null, qty };
}

/**
 * Prefills for section A: price converted to USD from the best/quoted price,
 * quantity from the MOQ (spec § 5.12).
 */
export function landedPrefill(supplier, fair) {
  const p = (supplier && supplier.products && supplier.products[0]) || null;
  if (!p) return { tc_price: '', tc_qty: '' };
  const v = num(p.bestPrice || p.price);
  const usd = p.currency === 'USD' ? v : toUSD(v, p.currency, fair);
  return {
    tc_price: usd != null && usd > 0 ? String(Number(usd.toFixed(4))) : '',
    tc_qty: p.moq || '',
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   RF1 — risk flags
   ═════════════════════════════════════════════════════════════════════════ */

/** Seven flags, in the prototype's exact order and with its exact wording. */
export function riskFlags(supplier) {
  const r = (supplier && supplier.review) || {};
  const flags = [];

  if (r.q_sampleReceived !== 'Yes') flags.push('No sample received');
  else if (r.q_sampleApproved !== 'Yes') flags.push('Sample not approved');

  if (r.c_status !== 'Verified') flags.push('Certification not verified');
  if (!r.sv_license) flags.push('Business license not on file');
  if (r.sv_type === 'Trading Company') flags.push('Trading company, not factory');
  if (!r.w_length) flags.push('No warranty info');
  if (!r.ii_hs) flags.push('No HS code');

  return flags;
}

/* ═══════════════════════════════════════════════════════════════════════════
   R1 / R2 — revisit ordering and samples to collect
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * R1 — the order the revisit list and the walking sheets are read in.
 *
 * This used to walk the fair geographically (phase, then hall, then booth).
 * With those fields gone there is nothing spatial left to sort on, so the
 * order is by company, numeric-aware — "Acme 10" after "Acme 9", not before.
 */
export function supplierSortKey(supplier) {
  return String((supplier && supplier.company) || '');
}

export function supplierSort(a, b) {
  return supplierSortKey(a).localeCompare(supplierSortKey(b), undefined, { numeric: true });
}

/** Every product flagged for a return visit, as {supplier, product} pairs. */
export function revisitList(suppliers) {
  return (suppliers || [])
    .slice()
    .sort(supplierSort)
    .flatMap((s) => (s.products || []).filter((p) => p.revisit).map((p) => ({ supplier: s, product: p })));
}

/**
 * R2 — samples to collect: not yet received, AND either ordered or paid for,
 * AND not being shipped (those arrive on their own).
 */
export function samplesToCollect(suppliers) {
  return (suppliers || []).flatMap((s) =>
    (s.products || [])
      .filter(
        (p) =>
          p.sample !== 'Received' &&
          (p.sample === 'Ordered' || num(p.samplePaid) > 0) &&
          p.sampleHandover !== 'Ship to forwarder' &&
          p.sampleHandover !== 'Ship to USA',
      )
      .map((p) => ({ supplier: s, product: p })),
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   B1 — the budget line
   ═════════════════════════════════════════════════════════════════════════ */

/** Sample + deposit actually handed over at this booth. */
export function paidAtBooth(supplier) {
  return ((supplier && supplier.products) || []).reduce(
    (a, p) => a + num(p.samplePaid) + num(p.depositPaid),
    0,
  );
}

export function fairCommitted(suppliers) {
  return (suppliers || []).reduce((a, s) => a + paidAtBooth(s), 0);
}

/** What the Strong products would cost at their MOQ, in USD. */
export function plannedStrong(suppliers, fair) {
  return (suppliers || []).reduce(
    (a, s) =>
      a +
      (s.products || [])
        .filter((p) => p.decision === 'Strong' && p.price && p.moq)
        .reduce((b, p) => {
          const v = num(p.bestPrice || p.price) * num(p.moq);
          const usd = p.currency === 'USD' ? v : toUSD(v, p.currency, fair);
          return b + (usd || 0);
        }, 0),
    0,
  );
}

export function budgetLine(suppliers, fair) {
  const committed = fairCommitted(suppliers);
  const budget = num(fair && fair.budget);
  return {
    committed,
    budget,
    over: budget > 0 && committed > budget,
    planned: plannedStrong(suppliers, fair),
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   E1 — quotes expiring
   ═════════════════════════════════════════════════════════════════════════ */

/** Products whose quote expires within 14 days; negative days = expired. */
export function quotesExpiring(suppliers) {
  return (suppliers || [])
    .flatMap((s) =>
      (s.products || [])
        .filter((p) => p.validUntil)
        .map((p) => ({ supplier: s, product: p, days: daysUntil(p.validUntil) })),
    )
    .filter((x) => x.days != null && x.days <= QUOTE_EXPIRY_DAYS)
    .sort((a, b) => a.days - b.days);
}

export function expiryLabel(days) {
  if (days == null) return '';
  if (days < 0) return 'Expired';
  if (days === 0) return 'Expires today';
  return `${days}d left`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   T1 — the Today card
   ═════════════════════════════════════════════════════════════════════════ */

/** Suppliers captured since local midnight. */
export function capturedToday(suppliers) {
  const start = startOfToday();
  return (suppliers || []).filter((s) => (s.createdAt || 0) >= start);
}

/* ═══════════════════════════════════════════════════════════════════════════
   C1 — contacts: at most one primary
   ═════════════════════════════════════════════════════════════════════════ */

/**
 * Saving a primary clears the others; if none is primary the FIRST becomes it.
 * Returns a new contacts array.
 */
export function normalizePrimary(contacts, justSavedId) {
  const list = (contacts || []).map((c) => ({ ...c }));
  if (!list.length) return list;

  const saved = justSavedId ? list.find((c) => c.id === justSavedId) : null;
  if (saved && saved.primary) {
    list.forEach((c) => {
      c.primary = c.id === justSavedId;
    });
    return list;
  }
  if (!list.some((c) => c.primary)) list[0].primary = true;
  return list;
}

/* ═══════════════════════════════════════════════════════════════════════════
   PL1 — plan linkage
   ═════════════════════════════════════════════════════════════════════════ */

/** found || matches.length > 0 — a skipped item counts as found too. */
export const planItemFound = (item) =>
  !!(item && (item.found === true || (item.matches || []).length > 0));

/** Push {supplierId, productId} into matches and mark the item found. */
export function linkPlanItem(fair, planId, supplierId, productId) {
  if (!fair || !planId) return fair;
  return {
    ...fair,
    plan: (fair.plan || []).map((item) => {
      if (item.id !== planId) return item;
      const matches = [...(item.matches || [])];
      if (!matches.some((m) => m.supplierId === supplierId && m.productId === productId)) {
        matches.push({ supplierId, productId });
      }
      return { ...item, matches, found: true };
    }),
  };
}

/** Plan progress for the "found/total" pill. */
export function planProgress(fair) {
  const plan = (fair && fair.plan) || [];
  return { found: plan.filter(planItemFound).length, total: plan.length };
}

/**
 * The matched products for a plan item, with the cheapest flagged `best` —
 * only when there is more than one, matching the prototype's "best price" label.
 */
export function planMatches(item, suppliers, fair) {
  const rows = ((item && item.matches) || [])
    .map(({ supplierId, productId }) => {
      const supplier = (suppliers || []).find((s) => s.id === supplierId);
      if (!supplier) return null;
      const product = (supplier.products || []).find((p) => p.id === productId);
      if (!product) return null;
      const v = num(product.bestPrice || product.price);
      const usd = product.currency === 'USD' ? v : toUSD(v, product.currency, fair);
      return { supplier, product, usd: usd || null };
    })
    .filter(Boolean);

  // `best` is a flag, not a tri-state: it is set only on the cheapest row, so
  // `if (row.best)` reads correctly and an unlabelled row has no property at
  // all. Only applied when there is something to compare against.
  if (rows.length > 1) {
    const priced = rows.filter((r) => r.usd != null && r.usd > 0);
    if (priced.length) {
      const min = Math.min(...priced.map((r) => r.usd));
      const winner = priced.find((r) => r.usd === min);
      if (winner) winner.best = true;
    }
  }
  return rows;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Checklist progress (spec § 5.10)
   ═════════════════════════════════════════════════════════════════════════ */

export function checklistProgress(fair) {
  const list = (fair && fair.checklist) || [];
  return { done: list.filter((i) => i.done).length, total: list.length };
}

/* ═══════════════════════════════════════════════════════════════════════════
   Dashboard tiles + search (spec § 5.3)
   ═════════════════════════════════════════════════════════════════════════ */

export function dashboardStats(suppliers) {
  const products = (suppliers || []).flatMap((s) => s.products || []);
  return {
    total: (suppliers || []).length,
    strong: (suppliers || []).filter((s) => supplierDecision(s) === 'Strong').length,
    maybe: (suppliers || []).filter((s) => supplierDecision(s) === 'Maybe').length,
    no: (suppliers || []).filter((s) => supplierDecision(s) === 'No').length,
    samples: products.filter((p) => p.sample === 'Ordered' || p.sample === 'Received').length,
    followUps: (suppliers || []).reduce((n, s) => n + pendingFollowUps(s), 0),
  };
}

/**
 * Global search (spec § 5.3): company, notes, every contact field and every
 * product's name / description / notes.
 */
export function matchesSearch(supplier, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const hay = [
    supplier.company,
    supplier.chinaNotes,
    ...(supplier.contacts || []).flatMap((c) => [
      c.phone,
      c.wechat,
      c.whatsapp,
      c.email,
      c.website,
      c.instagram,
    ]),
    ...(supplier.products || []).flatMap((p) => [p.name, p.description, p.notes]),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

// Re-exported so screens import rules and formatting from one place.
export {
  num,
  money,
  fmt,
  today,
  startOfToday,
  daysUntil,
  dateLabel,
  esc,
  digits,
  contactLabel,
} from './format';
